/**
 * ============================================================================
 * Iron Meridian - Turn Control Component
 * ============================================================================
 * 
 * Controls for processing turns and displaying turn status.
 * Premium design with polished button states and animations.
 */

import { useEventSound } from '../audio/hooks/useAudio.js'

export default function TurnControl({ gameState, actionCount, isProcessing, onProcessTurn }) {
  const { endTurn } = useEventSound()
  const phase = gameState?.phase || 'player_action'
  const turn = gameState?.currentTurn || 1

  const phaseConfig = {
    player_action: { label: 'Planning Phase', color: 'bg-green-500', pulse: true },
    processing: { label: 'Processing...', color: 'bg-yellow-500', pulse: false },
    ai_action: { label: 'AI Turn', color: 'bg-red-500', pulse: false },
    resolved: { label: 'Turn Resolved', color: 'bg-blue-500', pulse: false }
  }

  const currentPhase = phaseConfig[phase] || phaseConfig.player_action

  return (
    <div className="p-5 border-b border-war-room-600/30 bg-gradient-to-br from-war-room-800 via-war-room-800 to-war-room-700/50">
      {/* Phase & Turn Display */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${currentPhase.color} ${currentPhase.pulse ? 'animate-pulse' : ''}`}></div>
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wider">Current Phase</div>
            <div className="font-semibold text-white">{currentPhase.label}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-gray-500 uppercase tracking-wider">Turn</div>
          <div className="text-4xl font-bold text-war-room-accent tabular-nums leading-none">{turn}</div>
        </div>
      </div>

      {/* Action Summary */}
      <div className="bg-war-room-700/40 rounded-xl p-3 mb-4 border border-war-room-600/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-gray-400">📋</span>
            <span className="text-sm text-gray-300">Queued Actions</span>
          </div>
          <div className={`px-3 py-1 rounded-full text-sm font-bold ${
            actionCount > 0 
              ? 'bg-war-room-accent/20 text-war-room-accent' 
              : 'bg-war-room-700 text-gray-500'
          }`}>
            {actionCount}
          </div>
        </div>
      </div>

      {/* End Turn Button */}
      <button
        onClick={() => {
          endTurn()
          onProcessTurn()
        }}
        disabled={isProcessing}
        className={`
          w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all duration-300
          flex items-center justify-center gap-3 relative overflow-hidden group
          ${isProcessing 
            ? 'bg-war-room-700 text-gray-500 cursor-not-allowed' 
            : 'bg-gradient-to-r from-war-room-accent via-blue-500 to-war-room-accent bg-[length:200%_100%] text-white shadow-lg hover:shadow-glow active:scale-[0.98]'
          }
        `}
        style={!isProcessing ? { animation: 'gradient-shift 3s ease infinite' } : {}}
      >
        {isProcessing ? (
          <>
            <div className="w-5 h-5 border-2 border-gray-500 border-t-gray-300 rounded-full animate-spin"></div>
            <span>Processing Turn...</span>
          </>
        ) : (
          <>
            <span>End Turn</span>
            <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </>
        )}
      </button>

      {/* Helper text */}
      {!isProcessing && (
        <p className="text-xs text-gray-500 text-center mt-3">
          {actionCount > 0 
            ? `${actionCount} action${actionCount !== 1 ? 's' : ''} will be executed`
            : 'No actions queued — turn will be skipped'
          }
        </p>
      )}
      
      <style>{`
        @keyframes gradient-shift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
      `}</style>
    </div>
  )
}
